import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Watch, RefreshCw, Unplug, ArrowLeft, Check, Loader2, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_LOGOS = {
  apple_health: { initials: "AH", gradient: "from-pink-500 to-red-500" },
  garmin: { initials: "G", gradient: "from-blue-500 to-cyan-500" },
  strava: { initials: "S", gradient: "from-orange-500 to-red-500" },
  whoop: { initials: "W", gradient: "from-green-500 to-emerald-500" },
};

export default function WearablesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState("");
  const [connecting, setConnecting] = useState("");

  const loadIntegrations = useCallback(() => {
    api().get("/integrations").then(r => {
      setIntegrations(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  const connectDevice = async (provider) => {
    setConnecting(provider);
    try {
      await api().post("/integrations/connect", { provider });
      toast.success("Eszköz csatlakoztatva!");
      loadIntegrations();
    } catch {
      toast.error("Hiba a csatlakozásnál");
    } finally {
      setConnecting("");
    }
  };

  const disconnectDevice = async (provider) => {
    try {
      await api().post("/integrations/disconnect", { provider });
      toast.success("Eszköz leválasztva");
      loadIntegrations();
    } catch {
      toast.error("Hiba a leválasztásnál");
    }
  };

  const syncDevice = async (provider) => {
    setSyncing(provider);
    try {
      await api().post("/integrations/sync", { provider });
      toast.success("Szinkronizálás sikeres!");
      loadIntegrations();
    } catch {
      toast.error("Hiba a szinkronizálásnál");
    } finally {
      setSyncing("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const connected = integrations.filter(i => i.connected);
  const disconnected = integrations.filter(i => !i.connected);

  return (
    <div className="space-y-6 md:pt-20" data-testid="wearables-page">
      <div className="flex items-center gap-4 fade-in-up">
        <button
          data-testid="wearables-back-btn"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <ArrowLeft size={18} className="text-white" />
        </button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Wearable Integrációk</h1>
          <p className="text-sm text-gray-400">Csatlakoztasd eszközeid az Athlevity-hez</p>
        </div>
        <Watch className="text-[#00f260] ml-auto" size={24} />
      </div>

      {/* Status */}
      <GlassCard glow className="fade-in-up fade-in-up-1" testId="wearables-status">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#00f260]/10 flex items-center justify-center">
              <Wifi size={20} className="text-[#00f260]" />
            </div>
            <div>
              <p className="text-sm text-white font-medium">{connected.length} eszköz csatlakoztatva</p>
              <p className="text-xs text-gray-400">{disconnected.length} elérhető integráció</p>
            </div>
          </div>
          <Badge className="bg-[#00f260]/10 border-0 text-[#00f260] text-xs">
            {connected.length > 0 ? "Aktív" : "Nincs eszköz"}
          </Badge>
        </div>
      </GlassCard>

      {/* Connected Devices */}
      {connected.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs text-gray-400 uppercase tracking-wider fade-in-up">Csatlakoztatott eszközök</p>
          {connected.map((device, i) => {
            const logo = PROVIDER_LOGOS[device.provider] || PROVIDER_LOGOS.garmin;
            return (
              <GlassCard key={device.provider} className={`fade-in-up fade-in-up-${i + 2}`} testId={`device-${device.provider}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${logo.gradient} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-sm font-bold">{logo.initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-white font-medium">{device.name}</p>
                      <div className="w-2 h-2 rounded-full bg-[#00f260]" />
                    </div>
                    <p className="text-xs text-gray-400">{device.desc}</p>
                    {device.last_sync && (
                      <p className="text-[10px] text-gray-500 mt-1">Utolsó szinkronizálás: {new Date(device.last_sync).toLocaleString("hu-HU")}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`sync-${device.provider}`}
                      onClick={() => syncDevice(device.provider)}
                      disabled={syncing === device.provider}
                      className="w-9 h-9 rounded-lg bg-[#00f260]/10 flex items-center justify-center hover:bg-[#00f260]/20 transition-colors"
                    >
                      {syncing === device.provider ? (
                        <Loader2 size={16} className="animate-spin text-[#00f260]" />
                      ) : (
                        <RefreshCw size={16} className="text-[#00f260]" />
                      )}
                    </button>
                    <button
                      data-testid={`disconnect-${device.provider}`}
                      onClick={() => disconnectDevice(device.provider)}
                      className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                    >
                      <Unplug size={16} className="text-red-400" />
                    </button>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {/* Available Integrations */}
      {disconnected.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs text-gray-400 uppercase tracking-wider fade-in-up">Elérhető integrációk</p>
          {disconnected.map((device, i) => {
            const logo = PROVIDER_LOGOS[device.provider] || PROVIDER_LOGOS.garmin;
            return (
              <GlassCard key={device.provider} className={`fade-in-up fade-in-up-${i + 3}`} testId={`available-${device.provider}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${logo.gradient} opacity-50 flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-sm font-bold">{logo.initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium">{device.name}</p>
                    <p className="text-xs text-gray-400">{device.desc}</p>
                  </div>
                  <button
                    data-testid={`connect-${device.provider}`}
                    onClick={() => connectDevice(device.provider)}
                    disabled={connecting === device.provider}
                    className="neon-btn text-xs py-2 px-4"
                  >
                    {connecting === device.provider ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : "Csatlakozás"}
                  </button>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {/* Info */}
      <GlassCard className="fade-in-up" testId="wearables-info">
        <p className="text-xs text-gray-400 leading-relaxed">
          Az eszköz csatlakoztatásával az Athlevity automatikusan szinkronizálja az edzés, alvás és egészségügyi adataidat. 
          A szinkronizálás valós időben történik, és az adataid biztonságosan vannak tárolva.
        </p>
      </GlassCard>
    </div>
  );
}
